import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ShowCard from '@/components/feature/ShowCard';
import { categories, getShowsByCategory } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [sortBy, setSortBy] = useState<string>('rating');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, location.pathname]);

  const category = categories.find((c) => c.slug === slug);
  const allShows = getShowsByCategory(slug || '');

  const sortedShows = [...allShows].sort((a: Show, b: Show) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'year':
        return b.year - a.year;
      case 'episodes':
        return a.episodes - b.episodes;
      default:
        return 0;
    }
  });

  const filteredShows = statusFilter === 'all'
    ? sortedShows
    : sortedShows.filter((s: Show) => s.status === (statusFilter === 'completed' ? '已完结' : '更新中'));

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-500">分类未找到</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category.name },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="py-8 md:py-12 bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-4 flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200 bg-background-50 sticky top-16 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
              {categories.map((c) => (
                <a
                  key={c.slug}
                  href={`/category/${c.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === c.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <i className={`${c.icon} text-sm`}></i>
                  {c.name}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Filters & Sort */}
        <section className="py-4 bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400">筛选：</span>
                {[
                  { value: 'all', label: '全部' },
                  { value: 'completed', label: '已完结' },
                  { value: 'updating', label: '更新中' },
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      statusFilter === f.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 border border-background-200 cursor-pointer outline-none focus:border-primary-300"
                >
                  <option value="rating">按评分</option>
                  <option value="year">按年份</option>
                  <option value="episodes">按集数</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Show List */}
        <section className="py-8 md:py-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {filteredShows.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4" data-product-shop>
                {filteredShows.map((show: Show) => (
                  <ShowCard key={show.id} show={show} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-movie-2-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无符合条件的剧集</p>
              </div>
            )}

            <div className="mt-6 text-sm text-foreground-400">
              共 {filteredShows.length} 部{category.name}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': `${category.name} - 琪琪影院`,
          'description': `琪琪影院${category.name}专区，${category.description}，高清在线免费观看。`,
          'url': `https://qiyicinema.com/category/${slug}`,
          'isPartOf': {
            '@type': 'WebSite',
            'name': '琪琪影院',
            'url': 'https://qiyicinema.com',
          },
        }),
      }} />
    </div>
  );
}